import React, { ClipboardEvent, Fragment } from "react";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { HorizontalHeaders, VerticalSeasons } from "./utils/header.utils";
import { StyledTableCell } from "../../../components/ShipmentSummary/components/SummaryGrid/SummaryGrid";
import {
  BACKGROUND_GRAY,
  GREEN_05,
  LIGHT_BACKGROUND_GRAY,
  TABLE_BORDER_GRAY,
} from "../../../constants/css.constants";
import ClickableCell from "../../../components/ClickableCell/ClickableCell";
import Typography from "@mui/material/Typography";
import { formatNumbersWithCommas } from "../../../constants/utils";
import useEditRights from "../../../hooks/useEditRights";
import Table from '@mui/material/Table';

const defaultColSpan = 3;
type ForecastTableProps = {
  data: Array<Array<number>>;
  horizontalHeaders: HorizontalHeaders;
  updateData: (row: number, column: number) => (units: string) => void;
  tab: string;
  verticalMonths: string[];
  verticalSeasons: VerticalSeasons;
  monthsInFirstTwoSeasons: number;
  onPaste: (row: number, column: number) => (e: ClipboardEvent) => void;
  totals: number[];
  additionalHeaders: string[];
  setNextCell: (row: number, column: number) => () => void;
  selectedCell: { row: number; column: number };
};
const FuturesTable = ({
  data,
  horizontalHeaders,
  tab,
  verticalMonths,
  verticalSeasons,
  monthsInFirstTwoSeasons,
  onPaste,
  totals,
  additionalHeaders,
  updateData,
  selectedCell,
  setNextCell,
}: ForecastTableProps) => {
  const { canEdit } = useEditRights();
  const isTB = tab.toLowerCase() === "tb";
  const isDisabled = isTB || !canEdit;
  const calculateTotals = (data: number[][]) =>
    data.reduce((acc, row) => {
      const rowLength = row.length - 3;
      if (acc.length === 0) return row.slice(rowLength);
      return acc.map((val, i) => val + row[rowLength + i]);
    }, []);

  return (
    <TableContainer component={Paper}>
     <Table size="small">
      <TableHead>
        <TableRow>
          <StyledTableCell
            rowSpan={4}
            colSpan={defaultColSpan}
            cssProperties={{
              borderTop: TABLE_BORDER_GRAY,
              borderLeft: TABLE_BORDER_GRAY,
              fontSize: "24px",
            }}
            padding='none'
            size='small'
          >
            <Typography variant="h6">{tab}</Typography>
          </StyledTableCell>
        </TableRow>
        <TableRow>
          {horizontalHeaders.SEASONS.map((season) => (
            <StyledTableCell
              key={season.value}
              colSpan={season.colSpan}
              cssProperties={{
                borderTop: TABLE_BORDER_GRAY,
                minWidth: `${season.colSpan * 100}px`,
              }}
              padding='none'
              size='small'
            >
              {season.value}
            </StyledTableCell>
          ))}
          <StyledTableCell
            rowSpan={2}
            colSpan={3}
            cssProperties={{
              borderTop: TABLE_BORDER_GRAY,
              borderLeft: TABLE_BORDER_GRAY,
              backgroundColor: "#FFF",
              color: "black",
              fontSize: "24px",
            }}
            padding='none'
            size='small'
          ></StyledTableCell>
        </TableRow>
        <TableRow>
          {horizontalHeaders.QUARTERS.map((season, i) => (
            <StyledTableCell
              key={season.value + i}
              colSpan={season.colSpan}
              cssProperties={{
                borderTop: TABLE_BORDER_GRAY,
                width: `${season.colSpan * 100}px`,
              }}
              padding='none'
              size='small'
            >
              {season.value}
            </StyledTableCell>
          ))}
        </TableRow>
        <TableRow>
          {horizontalHeaders.MONTHS.map((season) => (
            <StyledTableCell key={season} cssProperties={{ width: "100px" }}
                             padding='none'
                             size='small'>
              {season}
            </StyledTableCell>
          ))}
          {additionalHeaders.map((season) => (
            <StyledTableCell
              key={season}
              cssProperties={{
                whiteSpace: "nowrap",
              }}
              padding='none'
              size='small'
            >
              {season}
            </StyledTableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {verticalSeasons.map((_, row) => {
          const verticalSeason = verticalSeasons[row];
          const rowSpan = verticalSeason.rowSpan;
          const offsetSpan = verticalSeasons[0].rowSpan;
          return (
            <Fragment key={`${row * 9 + 1}`}>
              <TableRow>
                <StyledTableCell
                  rowSpan={rowSpan + 1}
                  cssProperties={{
                    minWidth: "100px",
                    borderLeft: TABLE_BORDER_GRAY,
                  }}
                  padding='none'
                  size='small'
                >
                  {verticalSeason.value}
                </StyledTableCell>
              </TableRow>
              {verticalMonths
                .slice(
                  row === 0 ? 0 : (row - 1) * defaultColSpan + offsetSpan,
                  row * defaultColSpan + offsetSpan,
                )
                .map((month, innerRow) => {
                  const rowAccessor =
                    row === 0
                      ? innerRow
                      : (row - 1) * defaultColSpan + offsetSpan + innerRow;

                  return (
                    <TableRow key={`${month}-${row * 11 + 1}`}>
                      <StyledTableCell
                        colSpan={2}
                        cssProperties={{ minWidth: "100px" }}
                        padding='none'
                        size='small'
                      >
                        {month}
                      </StyledTableCell>
                      {horizontalHeaders.MONTHS.map((_, column) => {
                        const datum = data[rowAccessor][column];
                        const isShortRange = column < monthsInFirstTwoSeasons;
                        return (
                          <ClickableCell
                            disabled={isDisabled}
                            updateData={updateData(rowAccessor, column)}
                            type="number"
                            key={`${column}-${row * 9 + 1}`}
                            styles={
                              isDisabled && isShortRange
                                ? {
                                    backgroundColor: LIGHT_BACKGROUND_GRAY,
                                    color: "black",
                                  }
                                : isDisabled
                                  ? {
                                      backgroundColor: BACKGROUND_GRAY,
                                      color: "#FFF",
                                    }
                                  : isShortRange
                                    ? {
                                        backgroundColor: GREEN_05,
                                        color: "#000",
                                      }
                                    : {
                                        backgroundColor: "white",
                                        color: "#000",
                                      }
                            }
                            onPaste={onPaste(rowAccessor, column)}
                            transform={formatNumbersWithCommas}
                            value={datum}
                            isSelected={
                              selectedCell.row === rowAccessor &&
                              selectedCell.column === column
                            }
                            setNextCell={setNextCell(rowAccessor, column)}
                          />
                        );
                      })}
                      {data[rowAccessor]
                        .slice(data[rowAccessor].length - 3)
                        .map((datum) => {
                          return (
                            <ClickableCell
                              disabled
                              transform={formatNumbersWithCommas}
                              styles={{}}
                              type="number"
                              value={datum}
                            />
                          );
                        })}
                    </TableRow>
                  );
                })}
            </Fragment>
          );
        })}
        <TableRow>
          <StyledTableCell
            cssProperties={{
              minWidth: "100px",
              borderLeft: TABLE_BORDER_GRAY,
            }}
            colSpan={3}
            padding='none'
            size='small'
          >
            Futures Total
          </StyledTableCell>
          {totals.map((total) => (
            <StyledTableCell
              cssProperties={{
                backgroundColor: BACKGROUND_GRAY,
              }}
              padding='none'
              size='small'
            >
              {formatNumbersWithCommas(total)}
            </StyledTableCell>
          ))}
          {calculateTotals(data).map((datum) => (
            <ClickableCell
              disabled
              transform={formatNumbersWithCommas}
              styles={{}}
              type="number"
              value={datum}
            />
          ))}
        </TableRow>
      </TableBody>
     </Table>
    </TableContainer>
  );
};

export default FuturesTable;
